import { useContext, useEffect, useState } from "react";
import { Col, Row } from "reactstrap";
import Collapsable from "../../../components/Collapsable";
import DataTable from "../../../components/DataTable";
import { QuoteDataContext } from "../contexts/quoteDataContext";
import { formatCurrencyForTable } from "../functions/quoteFunctions";

// Incoming quote items that are both upfront and recurring will come 
// through as separate line items. This function combines them into a single 
// line item for display in the autotask table.  I am only bothering to update
// the fields that are used by the autotask table, but this could be expanded if needed.
const joinItemsById = (items) => {
    const itemMap = new Map();
    items.forEach(item => {
        const existing = itemMap.get(item.id);
        if (existing) {
            const wasRecurring = existing.isRecurring;

            existing.isUpfront = existing.isUpfront || item.isUpfront;
            existing.isRecurring = existing.isRecurring || item.isRecurring;

            if (item.isUpfront) {
                existing.upfrontCost = item.upfrontCost;
                existing.upfrontPrice = item.upfrontPrice;
            }

            // When P&L view splits one source item into multiple recurring-style rows,
            // keep the recurring fields from the best row for MR display, preferring a
            // monthly row over a spread annualized row when both exist.  There will be
            // two recurring rows for the same source item when the item has a recurring
            // cadence but also has some upfront cost/price that has a recurring salesforce
            // code that has been spread for the P&L view.
            const shouldUseRecurringFields =
                item.isRecurring && (
                    !wasRecurring ||
                    existing.recurringIntervalMonths == null ||
                    (existing.recurringIntervalMonths !== 1 && item.recurringIntervalMonths === 1)
                );

            if (shouldUseRecurringFields) {
                // These fields drive the MR columns, so they must come from the row that
                // represents the real recurring cadence rather than a spread annual value.
                existing.recurringCost = item.recurringCost;
                existing.recurringPrice = item.recurringPrice;
                existing.recurringIntervalMonths = item.recurringIntervalMonths;
            }

            existing.annualCost = (Number.isFinite(existing.annualCost) ? existing.annualCost : 0) + (Number.isFinite(item.annualCost) ? item.annualCost : 0);
            existing.annualPrice = (Number.isFinite(existing.annualPrice) ? existing.annualPrice : 0) + (Number.isFinite(item.annualPrice) ? item.annualPrice : 0);
        } else {
            itemMap.set(item.id, {...item});
        }
    });
    return Array.from(itemMap.values());
}

const AutotaskTable = ({header, quoteItems}) => {

    const quoteDataContext = useContext(QuoteDataContext);
    const { suppliers } = quoteDataContext;
    const [combinedQuoteItems, setCombinedQuoteItems] = useState(() => {
        if (!quoteItems) return [];
        return joinItemsById(quoteItems);
    });

    useEffect(() => {
        setCombinedQuoteItems(joinItemsById(quoteItems));
    }, [quoteItems]);

    const columns = [
        { key: "quantity", label: "Quantity" },
        { key: "autotaskCode", label: "Autotask Code", align: "left" },
        { key: "internalName", label: "Description", align: "left" },
        { key: "upfrontPrice", label: "Unit OO Price", render: (field) => formatCurrencyForTable(field) },
        { key: "upfrontCost", label: "Unit OO Cost", render: (field) => formatCurrencyForTable(field) },
        { key: "annualPrice", label: "Unit AR Price", render: (field) => formatCurrencyForTable(field) },
        { key: "annualCost", label: "Unit AR Cost", render: (field) => formatCurrencyForTable(field) },
        { key: "unitMRPrice", label: "Unit MR Price", render: (field, item) => item.recurringIntervalMonths === 1 ? formatCurrencyForTable(item.recurringPrice) : formatCurrencyForTable(null) },
        { key: "unitMRCost", label: "Unit MR Cost", render: (field, item) => item.recurringIntervalMonths === 1 ? formatCurrencyForTable(item.recurringCost) : formatCurrencyForTable(null) },
        { key: "supplierId", label: "Supplier", render: (field, item) =>  suppliers.find(supplier => supplier.id === item.supplierId)?.supplierName || "", align: "left"},
        { key: "vendorQuoteReference", label: "Vendor Quote Reference", align: "left", render: (field, item) => item.typeCustomAttributes.find(x => x.label === "Vendor Quote Reference")?.value || item.customAttributes.find(x => x.label === "Vendor Quote Reference")?.value || "" }
    ].map(col => ({
        align: "right",
        ...col
    }));

    return (
        <Collapsable header={"Autotask"} open fluid>
            <Row>
                <Col>
                    <DataTable
                        data={combinedQuoteItems}
                        columns={columns}
                        width="auto"
                    />
                </Col>
            </Row>       
        </Collapsable>  
    );
}
export default AutotaskTable;